import React, { useEffect, useState } from 'react';
import { MapPin, X, Plane, ShoppingBag, Hotel, Trophy, Cross } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const VENUE_ICON = {
  airport: Plane,
  mall: ShoppingBag,
  hotel: Hotel,
  stadium: Trophy,
  hospital: Cross,
};

/**
 * Phase 4 — Smart pickup assist.
 * When a pickup location matches a curated venue (airport, mall, etc.),
 * we surface this overlay with the recommended pickup bay + a tiny diagram
 * + an animated dot showing where the rider is.
 *
 * Props:
 *   pickupLat, pickupLng — the ride's pickup location
 *   onClose()
 *
 * The component fetches /api/driver/v2/venue-lookup and only renders if a
 * match exists. Auto-dismisses after 12s if untouched.
 */
const SmartPickupAssist = ({ pickupLat, pickupLng, onClose }) => {
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (pickupLat == null || pickupLng == null) return undefined;
    let cancelled = false;
    fetch(`${API}/api/driver/v2/venue-lookup?lat=${pickupLat}&lng=${pickupLng}`)
      .then((r) => r.json())
      .then((d) => { if (!cancelled) { setMatch(d?.match || null); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [pickupLat, pickupLng]);

  useEffect(() => {
    if (!match) return undefined;
    const t = setTimeout(() => onClose?.(), 12000);
    return () => clearTimeout(t);
  }, [match, onClose]);

  if (loading || !match) return null;

  const Icon = VENUE_ICON[match.type] || MapPin;

  return (
    <div
      data-testid="smart-pickup-assist"
      className="fixed top-20 left-3 right-3 z-[68] bg-white/95 backdrop-blur-md rounded-3xl shadow-[0_12px_40px_rgba(0,0,0,0.18)] border border-emerald-200 animate-slide-up"
    >
      <div className="flex items-start p-4 gap-3">
        <div className="shrink-0 w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
          <Icon className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-emerald-700">Smart pickup</p>
          <p className="text-[14px] font-bold text-gray-900 truncate" data-testid="smart-pickup-venue">{match.name}</p>
          <p className="text-[12px] text-gray-600 mt-0.5" data-testid="smart-pickup-hint">{match.hint}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Dismiss"
          data-testid="smart-pickup-close"
          className="shrink-0 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
        >
          <X className="w-4 h-4 text-gray-500" />
        </button>
      </div>

      {/* Tiny venue diagram */}
      <div className="mx-4 mb-4 rounded-2xl bg-gradient-to-br from-emerald-50 to-emerald-100 p-3" data-testid="smart-pickup-diagram">
        <VenueDiagram type={match.diagram || match.type} />
      </div>
    </div>
  );
};

/** SVG schematic + animated dot showing where rider is standing. */
const VenueDiagram = ({ type }) => {
  const dotPos = ({ // schematic coordinates inside a 200x80 viewBox
    airport: { x: 130, y: 40, label: 'Terminal 2 · Gate B' },
    mall: { x: 60, y: 30, label: 'North entrance' },
    hotel: { x: 100, y: 45, label: 'Porte-cochère' },
    stadium: { x: 160, y: 40, label: 'Gate 4 (East)' },
    hospital: { x: 70, y: 45, label: "Visitors' gate" },
  }[type]) || { x: 100, y: 40, label: 'Pickup' };

  return (
    <svg viewBox="0 0 200 80" className="w-full h-20">
      {/* Building shape */}
      <rect x="20" y="20" width="160" height="40" rx="6" fill="white" stroke="#16A34A" strokeWidth="1.5" />
      {/* Entrance markers */}
      <line x1="50" y1="60" x2="50" y2="68" stroke="#16A34A" strokeWidth="1.5" />
      <line x1="100" y1="60" x2="100" y2="68" stroke="#16A34A" strokeWidth="1.5" />
      <line x1="150" y1="60" x2="150" y2="68" stroke="#16A34A" strokeWidth="1.5" />
      {/* Driver path arrow */}
      <path d="M10 70 Q 90 70 ${dotPos.x} ${dotPos.y + 4}" stroke="#16A34A" strokeWidth="1.5" strokeDasharray="3 2" fill="none" opacity="0.5" />
      {/* Rider pulsing dot */}
      <circle cx={dotPos.x} cy={dotPos.y} r="6" fill="#16A34A" opacity="0.25">
        <animate attributeName="r" values="6;10;6" dur="1.6s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.4;0.1;0.4" dur="1.6s" repeatCount="indefinite" />
      </circle>
      <circle cx={dotPos.x} cy={dotPos.y} r="3.5" fill="#16A34A" />
      <text x={dotPos.x} y={dotPos.y - 10} textAnchor="middle" fontSize="9" fill="#15803D" fontWeight="600">
        {dotPos.label}
      </text>
    </svg>
  );
};

export default SmartPickupAssist;
